import { Component, inject, Injector, OnInit, ViewChild } from '@angular/core';
import { AuditRequest, AuditService } from '@markant-platform/shared/audit/data-access';
import { ActivatedRoute } from '@angular/router';
import { catchError, combineLatest, map, Observable, of, startWith, Subject, switchMap, tap, throwError } from 'rxjs';
import { Table, TableComponent } from '@markant-platform/shared/table/feature';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ConcatPipe, DefaultValuePipe } from '@markant-platform/shared/util-pipes';
import { AsyncPipe, KeyValuePipe, NgIf } from '@angular/common';
import { TableDraftBuilder } from '@markant-platform/shared/table/model';
import { AUDIT_TABLE_HEADER_AND_CELLS } from './configuration/audit-overview.config';
import { UserService } from '@markant-platform/shared/user/data-access';

import { CMSLanguageService } from '@markant-platform/shared/data-access';
import { SnackbarService } from '@markant-platform/shared/snackbar/feature';
import { AuditChangeItem, ChangeSetTransformer } from '@markant-platform/shared/audit/configuration';
import { MarkantElementsModule, MarkantFilterGroup } from '@markant/elements-angular';
import { Filter, mapActiveToMarkantFilter } from '@markant-platform/shared/ui-filter';
import { MarkantSelectOption } from '@markant-platform/shared/util-helpers';
import { UiPageHeaderComponent } from '@markant-platform/shared/ui-components';

/**
 * Provides an overview of all audited changes of an entity.<br/>
 * To enable this component to work two attributes **need** to be specified in the router path:
 * <ul>
 *   <li>_**data: transformer**_ -  defines the transformer function to transform the entries to changeset data</li>
 *   <li>_**resolve: entity**_ - to refer to the correct entity and display a custom mapped name, the entity need to be resolved individually.
 *   This will be done by using a resolver to push the data into the component.</li>
 * </ul>
 */
@Component({
  selector: 'markant-platform-audit-overview',
  standalone: true,
  imports: [
    UiPageHeaderComponent,
    TranslocoPipe,
    ConcatPipe,
    TableComponent,
    AsyncPipe,
    MarkantElementsModule,
    NgIf,
    DefaultValuePipe,
    KeyValuePipe,
  ],
  templateUrl: './audit-overview.component.html',
  styleUrl: './audit-overview.component.scss',
})
export class AuditOverviewComponent extends Table implements OnInit {
  @ViewChild(MarkantFilterGroup, { static: false }) filterGroup?: MarkantFilterGroup;

  override tableDraft = new TableDraftBuilder(AUDIT_TABLE_HEADER_AND_CELLS).disableRowLink().build();

  auditRequest!: AuditRequest;
  protected entityName = '';
  protected hasNextPage = false;
  protected loadFurtherData$: Subject<void> = new Subject();
  protected filter?: Filter<MarkantSelectOption>;

  private entities: AuditChangeItem[] = [];
  private transformer!: ChangeSetTransformer;

  private readonly PAGE_SIZE = 50;
  private readonly userService: UserService = inject(UserService);
  private readonly auditService: AuditService = inject(AuditService);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly cmsLanguageService: CMSLanguageService = inject(CMSLanguageService);
  private readonly snackbarService: SnackbarService = inject(SnackbarService);
  private readonly injector: Injector = inject(Injector);
  private readonly translocoService: TranslocoService = inject(TranslocoService);

  constructor() {
    super();
  }

  override ngOnInit(): void {
    const { transformer, context } = this.route.snapshot.data;
    this.auditRequest = {
      context: context.context,
      entityTypes: context.entryTypes,
      entitiesNumber: this.PAGE_SIZE,
    };

    const TransformerClass = transformer;
    if (!TransformerClass) {
      throw new Error('A transformer must be configured! Please configure one an pass it via router data');
    }
    this.transformer = this.injector.get<ChangeSetTransformer>(TransformerClass);
    this.entityName = context.name;

    combineLatest([
      this.cmsLanguageService.activeCMSLanguage$.pipe(
        tap(() => {
          this.entities = [];
          this.auditRequest.toDate = undefined;
        })
      ),
      this.loadFurtherData$.pipe(startWith(null)),
      this.tableParams$.pipe(
        tap(() => {
          this.entities = [];
          this.auditRequest.toDate = undefined;
        })
      ),
    ])
      .pipe(
        tap(() => this.isLoading$.next(true)),
        switchMap(() => {
          const { activeFilter, searchQuery } = this.tableParams$.value;
          return this.auditService
            .getAuditEntriesByContext({
              ...this.auditRequest,
              filters: activeFilter,
              query: searchQuery,
            } as AuditRequest)
            .pipe(
              tap(entries => entries.length && (this.auditRequest.toDate = entries[entries.length - 1].modifiedAt)),
              map(this.transformer.transform),
              switchMap(this.resolveUserNames),
              catchError(err => {
                return throwError(() => err);
              })
            );
        }),
        catchError(error => {
          this.snackbarService.showErrorSnackbarWithTransloco('audit.error', { traceId: error?.error?.traceId });

          return of([] as AuditChangeItem[]);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(result => {
        this.entities = [...this.entities, ...result];
        this.hasNextPage = result.length >= this.PAGE_SIZE;
        this.filter = this.transformer.provideFilters();
        const activeFilters = mapActiveToMarkantFilter(
          this.tableParams$.value.activeFilter,
          this.filter,
          this.translocoService
        );
        this.filterGroup?.updateActiveFilters(() => activeFilters).then();
        this.reload(this.entities);
      });
  }

  loadFurtherData(): void {
    this.loadFurtherData$.next();
  }

  private readonly resolveUserNames = (entries: AuditChangeItem[]): Observable<AuditChangeItem[]> => {
    if (!entries.length) {
      return of([]);
    }

    const userIds = new Set(entries.map(({ modifiedBy }) => modifiedBy));
    return this.userService.getUserNames([...userIds]).pipe(
      map(({ userNames }) => {
        return entries.map(auditEntry => {
          const user = userNames[auditEntry.modifiedBy];

          if (!user) {
            return auditEntry;
          }

          return {
            ...auditEntry,
            modifiedBy: `${user.firstName} ${user.lastName}`,
          };
        });
      })
    );
  };
}
